import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import DropCard from '../components/dashboard/DropCard';
import MerchCard from '../components/dashboard/MerchCard';
import DashboardStats from '../components/dashboard/DashboardStats';
import UploadDropModal from '../components/dashboard/UploadDropModal';
import EditDropModal from '../components/dashboard/EditDropModal';
import MerchModal from '../components/dashboard/MerchModal';
import AccessTierManager from '../components/dashboard/access/AccessTierManager';
import AccessSessionsTable from '../components/dashboard/access/AccessSessionsTable';
import GrantAccessModal from '../components/dashboard/access/GrantAccessModal';
import { useDrops, useMerch } from '../hooks/useDrops';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/nav/Navbar';
import EmptyState from '../components/common/EmptyState';

const {
  FiPlus, FiMusic, FiShoppingBag, FiRefreshCw,
  FiAlertCircle, FiGrid, FiList, FiShield, FiUserPlus,
  FiLock, FiArrowLeft, FiClock, FiMail,
} = FiIcons;

const TABS = [
  { key: 'drops',  label: 'Drops',       icon: FiMusic       },
  { key: 'merch',  label: 'Merchandise', icon: FiShoppingBag },
  { key: 'access', label: 'Access Mgmt', icon: FiShield      },
];

/* ── Access Denied Screen ── */
const AccessDenied = ({ profile, user }) => {
  const navigate = useNavigate();
  const status = profile?.access_status;

  const states = {
    pending: {
      icon: FiClock,
      color: 'text-amber-400',
      bg: 'bg-amber-500/5 border-amber-500/20',
      title: 'Verification Pending',
      desc: 'Your creator application is under review. Our team typically responds within 24–48 hours.',
      sub: 'You\'ll receive an email once a decision has been made.',
    },
    rejected: {
      icon: FiMail,
      color: 'text-red-400',
      bg: 'bg-red-500/5 border-red-500/20',
      title: 'Application Not Approved',
      desc: 'Unfortunately your creator application was not approved at this time.',
      sub: 'You can reapply or contact support for more information.',
    },
    default: {
      icon: FiLock,
      color: 'text-zinc-400',
      bg: 'bg-zinc-900/50 border-zinc-800',
      title: 'Creator Access Required',
      desc: 'The Creator Studio is only available to verified artists and labels.',
      sub: 'Sign up as an artist or label and submit a verification request to gain access.',
    },
  };

  const state = states[status] || states.default;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[500px] h-[500px] top-[-10%] right-[-10%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-10%] animate-caustic" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md space-y-8 text-center">

          {/* Back */}
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-600 hover:text-white transition-colors mx-auto group">
            <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
              <SafeIcon icon={FiArrowLeft} className="text-sm" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest">Back</span>
          </button>

          <div className={`glass-strong rounded-[2.5rem] p-10 space-y-6 border ${state.bg}`}>
            {/* Icon */}
            <div className={`w-20 h-20 rounded-3xl mx-auto flex items-center justify-center ${state.bg} border`}>
              <SafeIcon icon={state.icon} className={`text-4xl ${state.color}`} />
            </div>

            {/* Logo */}
            <img
              src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
              alt="Vision Direct"
              className="h-10 w-10 object-contain mx-auto drop-shadow-[0_0_12px_rgba(255,102,204,0.4)]"
            />

            <div className="space-y-3">
              <h1 className="text-3xl font-black uppercase tracking-tight text-white">{state.title}</h1>
              <p className="text-zinc-400 font-bold text-sm leading-relaxed">{state.desc}</p>
              <p className="text-zinc-600 font-bold text-xs leading-relaxed">{state.sub}</p>
            </div>

            {/* Status badge */}
            {status && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-black uppercase tracking-widest ${state.bg} ${state.color}`}>
                <SafeIcon icon={state.icon} className="text-xs" />
                {status === 'pending' ? 'Under Review' : status === 'rejected' ? 'Not Approved' : status}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-2">
              {(!status || status === 'rejected') && (
                <Link to="/auth"
                  className="w-full py-4 btn-brand rounded-2xl text-xs text-center">
                  Apply as Creator
                </Link>
              )}
              <Link to="/"
                className="w-full py-4 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all text-center">
                Back to Home
              </Link>
            </div>
          </div>

          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-700">
            Vision Direct Creator Studio · Verified Artists &amp; Labels Only
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Main Dashboard ── */
const Dashboard = () => {
  const { user, profile } = useAuth();

  // Not logged in
  if (!user) return <AccessDenied profile={null} user={null} />;

  // Fan or no profile — no access
  const role = profile?.role;
  const status = profile?.access_status;
  const isApprovedCreator = (role === 'artist' || role === 'label') && status === 'approved';

  if (!isApprovedCreator) return <AccessDenied profile={profile} user={user} />;

  return <DashboardContent user={user} profile={profile} />;
};

/* ── Dashboard Content (approved creators only) ── */
const DashboardContent = ({ user, profile }) => {
  const artistId = profile?.artist_id || null;

  const { drops, loading: dropsLoading, error: dropsError, refetch: refetchDrops, createDrop, updateDrop, deleteDrop, togglePublish } = useDrops(artistId);
  const { merch, loading: merchLoading, refetch: refetchMerch, createMerch, updateMerch, deleteMerch } = useMerch(artistId);

  const [tab, setTab]                 = useState('drops');
  const [accessTab, setAccessTab]     = useState('tiers');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMerchModal, setShowMerchModal]   = useState(false);
  const [showGrantModal, setShowGrantModal]   = useState(false);
  const [editingDrop, setEditingDrop]   = useState(null);
  const [editingMerch, setEditingMerch] = useState(null);
  const [dropFilter, setDropFilter]     = useState('all');
  const [viewMode, setViewMode]         = useState('grid');

  const filteredDrops = drops.filter(d => {
    if (dropFilter === 'published') return d.is_published;
    if (dropFilter === 'drafts')    return !d.is_published;
    if (dropFilter === 'pwyw')      return d.min_price === 0;
    return true;
  });

  const handleMerchSave = async (id, form) => id ? updateMerch(id, form) : createMerch(form);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar />

      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink  w-[600px] h-[600px] top-[-15%] right-[-10%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-5%]  animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-12 space-y-10">

          {/* Header */}
          <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-white/5 pb-10">
            <div className="space-y-2">
              <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Creator Studio</span>
              <h1 className="text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">Dashboard</h1>
              <p className="text-zinc-500 font-bold">
                {profile?.display_name || user.email?.split('@')[0]} · Upload drops, set paywalls &amp; manage merch
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { refetchDrops(); refetchMerch(); }}
                className="flex items-center gap-2 px-5 py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                <SafeIcon icon={FiRefreshCw} className="text-sm" />
              </button>
              {tab === 'drops' && (
                <button onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2 px-6 py-3 btn-brand rounded-2xl text-xs">
                  <SafeIcon icon={FiPlus} className="text-sm" /> Upload Drop
                </button>
              )}
              {tab === 'merch' && (
                <button onClick={() => { setEditingMerch({}); setShowMerchModal(true); }}
                  className="flex items-center gap-2 px-6 py-3 btn-brand rounded-2xl text-xs">
                  <SafeIcon icon={FiPlus} className="text-sm" /> Add Merch
                </button>
              )}
              {tab === 'access' && (
                <button onClick={() => setShowGrantModal(true)}
                  className="flex items-center gap-2 px-6 py-3 btn-brand rounded-2xl text-xs">
                  <SafeIcon icon={FiUserPlus} className="text-sm" /> Grant Access
                </button>
              )}
            </div>
          </header>

          <DashboardStats drops={drops} merch={merch} />

          {!artistId && (
            <div className="flex items-start gap-4 p-5 bg-amber-500/5 border border-amber-500/20 rounded-2xl">
              <SafeIcon icon={FiAlertCircle} className="text-amber-400 text-xl flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-black uppercase tracking-widest text-amber-400">Artist Profile Required</p>
                <p className="text-sm font-bold text-zinc-400">Your profile must be linked to an artist account to upload drops and manage merch.</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex gap-1.5 bg-zinc-900/60 border border-white/5 rounded-2xl p-1.5">
              {TABS.map(({ key, label, icon }) => (
                <button key={key} onClick={() => setTab(key)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                    tab === key ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
                  }`}>
                  <SafeIcon icon={icon} className="text-sm" /> {label}
                  {key !== 'access' && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${tab === key ? 'bg-black/20' : 'bg-zinc-800'}`}>
                      {key === 'drops' ? drops.length : merch.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {tab === 'drops' && (
              <div className="flex items-center gap-3">
                <div className="flex gap-1.5">
                  {['all', 'published', 'drafts', 'pwyw'].map(f => (
                    <button key={f} onClick={() => setDropFilter(f)}
                      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        dropFilter === f
                          ? 'bg-gradient-to-r from-brand-pink to-brand-coral text-white'
                          : 'glass-card text-zinc-500 hover:text-white'
                      }`}>
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1 glass-card rounded-xl p-1">
                  {[{ mode: 'grid', icon: FiGrid }, { mode: 'list', icon: FiList }].map(({ mode, icon }) => (
                    <button key={mode} onClick={() => setViewMode(mode)}
                      className={`p-2 rounded-lg transition-all ${viewMode === mode ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'}`}>
                      <SafeIcon icon={icon} className="text-sm" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Drops */}
          {tab === 'drops' && (
            <>
              {dropsError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl"><p className="text-red-400 text-xs font-black">{dropsError}</p></div>}
              {dropsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-zinc-700 border-t-brand-pink rounded-full animate-spin" />
                </div>
              ) : filteredDrops.length > 0 ? (
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                  {filteredDrops.map(d => (
                    <DropCard key={d.id} drop={d} onTogglePublish={togglePublish} onDelete={deleteDrop} onEdit={setEditingDrop} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title={dropFilter === 'all' ? 'No drops yet' : `No ${dropFilter} drops`}
                  subtitle={dropFilter === 'all' ? 'Upload your first drop to start earning.' : 'Try a different filter.'}
                  action={dropFilter === 'all' && artistId ? { label: 'Upload Drop', onClick: () => setShowUploadModal(true) } : undefined}
                />
              )}
            </>
          )}

          {/* Merch */}
          {tab === 'merch' && (
            <>
              {merchLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-zinc-700 border-t-brand-pink rounded-full animate-spin" />
                </div>
              ) : merch.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {merch.map(m => (
                    <MerchCard key={m.id} item={m} onEdit={(item) => { setEditingMerch(item); setShowMerchModal(true); }} onDelete={deleteMerch} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No merch listed"
                  subtitle="Add merchandise to prompt fans after they unlock your drops."
                  action={artistId ? { label: 'Add Merch Item', onClick: () => { setEditingMerch({}); setShowMerchModal(true); } } : undefined}
                />
              )}
            </>
          )}

          {/* Access Management */}
          {tab === 'access' && (
            <div className="space-y-8">
              <div className="flex gap-1.5 glass-card rounded-2xl p-1.5 w-fit">
                {[{ key: 'tiers', label: 'Access Tiers' }, { key: 'sessions', label: 'Fan Sessions' }].map(({ key, label }) => (
                  <button key={key} onClick={() => setAccessTab(key)}
                    className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                      accessTab === key ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              {accessTab === 'tiers' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-6 glass-card rounded-2xl space-y-4">
                    <div className="pb-4 border-b border-white/5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-brand-pink">Step 1</p>
                      <h3 className="text-lg font-black text-white uppercase tracking-tight mt-1">Define Access Tiers</h3>
                      <p className="text-xs font-bold text-zinc-500 mt-1">Create named levels (Free, Premium, VIP) to categorize content access.</p>
                    </div>
                    {artistId ? <AccessTierManager artistId={artistId} /> : <p className="text-xs font-bold text-zinc-600">Artist profile required.</p>}
                  </div>

                  <div className="space-y-4">
                    <div className="p-6 bg-brand-pink/5 border border-brand-pink/15 rounded-2xl space-y-5">
                      <p className="text-xs font-black uppercase tracking-widest text-brand-pink">How Access Rules Work</p>
                      {[
                        { step: '1', title: 'Create Access Tiers', desc: 'Define named levels like Free, Premium, VIP with colors and ranks.' },
                        { step: '2', title: 'Assign Rules to Drops', desc: 'On each Drop card, set the required tier, minimum payment, and expiry.' },
                        { step: '3', title: 'Fans Unlock Content', desc: 'When a fan pays, an access session is created automatically.' },
                        { step: '4', title: 'Monitor & Revoke', desc: 'View all fan sessions in the Sessions tab. Revoke access any time.' },
                      ].map(({ step, title, desc }) => (
                        <div key={step} className="flex items-start gap-4">
                          <div className="w-7 h-7 rounded-lg bg-brand-pink/10 border border-brand-pink/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-black text-brand-pink">{step}</span>
                          </div>
                          <div>
                            <p className="text-xs font-black text-white uppercase tracking-tight">{title}</p>
                            <p className="text-[10px] font-bold text-zinc-500 mt-0.5">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-6 glass-card rounded-2xl space-y-3">
                      <p className="text-xs font-black uppercase tracking-widest text-zinc-500">Quick Actions</p>
                      <button onClick={() => setShowGrantModal(true)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-brand-pink/10 border border-brand-pink/20 rounded-xl hover:bg-brand-pink/15 transition-all">
                        <div className="flex items-center gap-3">
                          <SafeIcon icon={FiUserPlus} className="text-brand-pink text-base" />
                          <div className="text-left">
                            <p className="text-xs font-black uppercase tracking-widest text-brand-pink">Manual Grant</p>
                            <p className="text-[10px] font-bold text-zinc-600">Give a specific fan direct access</p>
                          </div>
                        </div>
                        <span className="text-[10px] font-black text-brand-pink">→</span>
                      </button>
                      <button onClick={() => setAccessTab('sessions')}
                        className="w-full flex items-center justify-between px-4 py-3 glass-subtle rounded-xl hover:border-white/10 transition-all">
                        <div className="flex items-center gap-3">
                          <SafeIcon icon={FiShield} className="text-zinc-400 text-base" />
                          <div className="text-left">
                            <p className="text-xs font-black uppercase tracking-widest text-zinc-300">View Sessions</p>
                            <p className="text-[10px] font-bold text-zinc-600">See all active fan access</p>
                          </div>
                        </div>
                        <span className="text-[10px] font-black text-zinc-600">→</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {accessTab === 'sessions' && artistId && (
                <div className="space-y-4">
                  <div className="pb-4 border-b border-white/5">
                    <h3 className="text-lg font-black text-white uppercase tracking-tight">Fan Access Sessions</h3>
                    <p className="text-xs font-bold text-zinc-500 mt-1">All access grants for your drops. Monitor, filter, and revoke as needed.</p>
                  </div>
                  <AccessSessionsTable artistId={artistId} contentType="drop" />
                </div>
              )}
              {accessTab === 'sessions' && !artistId && (
                <p className="text-xs font-bold text-zinc-600">Artist profile required to view sessions.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {showUploadModal && <UploadDropModal onClose={() => setShowUploadModal(false)} onCreate={createDrop} />}
      {editingDrop && <EditDropModal drop={editingDrop} onClose={() => setEditingDrop(null)} onSave={updateDrop} />}
      {showMerchModal && (
        <MerchModal item={editingMerch?.id ? editingMerch : null}
          onClose={() => { setShowMerchModal(false); setEditingMerch(null); }} onSave={handleMerchSave} />
      )}
      {showGrantModal && <GrantAccessModal artistId={artistId} drops={drops} onClose={() => setShowGrantModal(false)} />}
    </div>
  );
};

export default Dashboard;